import { useQuery } from '@tanstack/react-query';
import { logout as logoutRoute, type MeRes, me as meRoute } from '#shared/api/auth';
import { getConfig } from '#shared/api/config';
import type { ConfigRes } from '#shared/schemas/config';
import { api, isApiError, isUnauthenticated } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { useSignals } from '@/lib/signals';

/**
 * Who is signed in on this phone. `null` means "not signed in" (Welcome shows), undefined
 * means "not known yet". The answer is persisted with the query cache, so a phone with no
 * signal still opens straight into the tabs.
 */
export const ME_KEY = ['auth', 'me'] as const;
export const CONFIG_KEY = ['config'] as const;

export async function fetchMe(): Promise<MeRes | null> {
  try {
    const res = await api.call(meRoute, {});
    useSignals.getState().clearSignedOut();
    return res;
  } catch (e) {
    if (isUnauthenticated(e)) return null;
    throw e;
  }
}

/**
 * True when `me` failed because the phone never reached the server (fetch threw a TypeError: no
 * signal, DNS), as opposed to the server answering with an error. Query retries do not pause on a
 * cold load offline (the online manager only learns about a change from events, and none fires
 * before the page exists), so they run out and the query ends in `error`. A signed-out phone with
 * no signal has no cached answer to fall back on and should still show Welcome, not "Something broke".
 */
export function noSignal(e: unknown): boolean {
  return e !== null && e !== undefined && !isApiError(e);
}

export function useMe() {
  // 'always': a restored answer is shown at once (so a phone with no signal opens into the tabs),
  // but with signal it is checked again, so a cached sign-in state can never outlive the cookie.
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 5 * 60_000, refetchOnMount: 'always' });
}

export function useConfig() {
  return useQuery<ConfigRes>({
    queryKey: CONFIG_KEY,
    queryFn: async () => {
      const cfg = await api.call(getConfig, {});
      useSignals.getState().sawServerNow(cfg.serverNow);
      return cfg;
    },
    staleTime: 10 * 60_000,
  });
}

/** After join, relogin or bootstrap: refetch me so the shell switches to the tabs. */
export async function signedIn(): Promise<void> {
  useSignals.getState().clearSignedOut();
  await queryClient.invalidateQueries({ queryKey: ME_KEY });
}

/** Sign out on this phone: tell the server, then forget every cached query. */
export async function signOut(): Promise<void> {
  try {
    await api.call(logoutRoute, {});
  } catch {
    // Offline or already gone: we still forget locally.
  }
  queryClient.clear();
  queryClient.setQueryData(ME_KEY, null);
}

/** Wire the 401 signal from the API layer to the cached me. */
export function watchSignOut(): () => void {
  return useSignals.subscribe((s, prev) => {
    if (s.signedOutAt && s.signedOutAt !== prev.signedOutAt) queryClient.setQueryData(ME_KEY, null);
  });
}
